/**
 * Client-side PDF generator for AMAL VBC documents using pdfmake.
 * Mirrors the layout produced by /backend/pdf_gen.py so users can generate
 * documents while offline. Used as a fallback when /api/documents/generate is unreachable.
 */
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";

pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfFonts.vfs;

const GREEN = "#00A859";
const DARK = "#0F172A";
const GREY = "#475569";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_a7620a69-63ea-4088-b703-b0ffc8f627c2/artifacts/6j219w5w_imagesk.png";

let _logoDataUrl = null;
async function getLogoDataUrl() {
  if (_logoDataUrl) return _logoDataUrl;
  try {
    const res = await fetch(LOGO_URL);
    const blob = await res.blob();
    _logoDataUrl = await new Promise((resolve) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.readAsDataURL(blob);
    });
    return _logoDataUrl;
  } catch {
    return null;
  }
}

const fmtNum = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? "").replace(/[^\d.-]/g, "")) || 0;
  return new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(n);
};

const todayFr = () =>
  new Date().toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });

function header(title, docNumber, logoData) {
  return {
    columns: [
      logoData ? { image: logoData, width: 60 } : { text: "", width: 60 },
      {
        stack: [
          { text: "AMAL VBC", style: "clubName" },
          { text: "Cotonou, Bénin — Saison 2025-2026", style: "clubSubtitle" },
          { text: "amalvbc@yahoo.com  •  +229 01 97 57 95 82", style: "clubSubtitle" },
        ],
        margin: [10, 5, 0, 0],
      },
      {
        stack: [
          { text: title.toUpperCase(), style: "docTitle", alignment: "right" },
          { text: `N° ${docNumber}`, style: "docNumber", alignment: "right" },
          { text: `Cotonou, le ${todayFr()}`, style: "docNumber", alignment: "right" },
        ],
        width: "auto",
      },
    ],
    margin: [0, 0, 0, 15],
  };
}

function divider() {
  return { canvas: [{ type: "line", x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1.5, lineColor: GREEN }], margin: [0, 0, 0, 12] };
}

function signatures() {
  return {
    margin: [0, 30, 0, 0],
    columns: [
      {
        stack: [
          { text: "Le Propriétaire et Gérant", style: "sigRole" },
          { text: "KEKERE Pamphile Dotou", style: "sigName" },
          { text: "_______________________________", margin: [0, 20, 0, 0] },
        ],
      },
      {
        stack: [
          { text: "Le Président du Bureau Exécutif", style: "sigRole" },
          { text: "NOUNAWON F. Parfait Constant", style: "sigName" },
          { text: "_______________________________", margin: [0, 20, 0, 0] },
        ],
      },
    ],
  };
}

const styles = {
  clubName: { fontSize: 18, bold: true, color: GREEN },
  clubSubtitle: { fontSize: 8, color: GREY },
  docTitle: { fontSize: 14, bold: true, color: DARK },
  docNumber: { fontSize: 8, color: GREY },
  body: { fontSize: 11, alignment: "justify", margin: [0, 0, 0, 8] },
  sigRole: { fontSize: 9, color: GREY, alignment: "center" },
  sigName: { fontSize: 11, bold: true, alignment: "center" },
  tableHeader: { bold: true, fillColor: GREEN, color: "white", fontSize: 10 },
  tableLabel: { bold: true, color: GREEN, fontSize: 10 },
  footerText: { fontSize: 7, color: GREY, alignment: "center", italics: true },
};

function buildDocDef(title, docNumber, bodyContent, logoData, opts = {}) {
  return {
    pageSize: "A4",
    pageMargins: [40, 40, 40, 55],
    content: [header(title, docNumber, logoData), divider(), ...bodyContent, ...(opts.noSign ? [] : [signatures()])],
    styles,
    defaultStyle: { font: "Roboto", fontSize: 11 },
    footer: (current, total) => ({
      stack: [
        { canvas: [{ type: "line", x1: 40, y1: 0, x2: 555, y2: 0, lineWidth: 0.5, lineColor: "#E2E8F0" }] },
        { text: `AMAL VBC — Document généré le ${todayFr()} • Page ${current}/${total}`, style: "footerText", margin: [0, 5, 0, 0] },
        { text: "Plateforme développée par WSDK CONNECT", style: "footerText" },
      ],
      margin: [40, 10, 40, 10],
    }),
  };
}

// ---------- Template builders ----------
function tplAttestationDon(d, n) {
  const montant = fmtNum(d.amount);
  return [
    { text: `Je soussigné, NOUNAWON F. Parfait Constant, Président du Bureau Exécutif du club AMAL VBC, atteste avoir reçu de ${d.donor_name || "—"} un don d'un montant de ${montant} FCFA.`, style: "body" },
    { text: `Ce don a été effectué le ${d.date || todayFr()} et est destiné à : ${d.purpose || "soutien aux activités du club"}.`, style: "body" },
    { text: "La présente attestation est délivrée pour servir et valoir ce que de droit.", style: "body" },
    { text: `Fait à Cotonou, le ${todayFr()}`, style: "body", margin: [0, 10, 0, 0] },
  ];
}

function tplRecuDon(d, n) {
  const montant = fmtNum(d.amount);
  return [
    { text: `Reçu officiel n° ${n}`, style: "body", bold: true },
    { text: `Le club AMAL VBC reconnaît avoir reçu de ${d.donor_name || "—"} la somme de ${montant} FCFA.`, style: "body" },
    { text: `Date de versement : ${d.date || todayFr()}`, style: "body" },
    { text: `Mode de règlement : ${d.payment_method || "Espèces"}`, style: "body" },
    { text: "Ce reçu est remis au donateur pour valoir ce que de droit.", style: "body" },
  ];
}

function tplContratTravail(d) {
  return [
    { text: "Entre les soussignés :", style: "body", bold: true },
    { text: "Le club AMAL VBC, représenté par son Propriétaire et Gérant, KEKERE Pamphile Dotou, ci-après dénommé « l'Employeur »,", style: "body" },
    { text: "Et", style: "body" },
    { text: `${d.employee_name || "—"}, ci-après dénommé(e) « le Salarié »,`, style: "body" },
    { text: "Il a été convenu et arrêté ce qui suit :", style: "body", bold: true },
    { text: `Article 1 — Engagement : Le Salarié est engagé en qualité de ${d.function || "—"} à compter du ${d.start_date || todayFr()}.`, style: "body" },
    { text: `Article 2 — Durée : Le présent contrat est conclu pour une durée ${d.duration || "Indéterminée (CDI)"}.`, style: "body" },
    { text: `Article 3 — Rémunération : Le Salarié percevra une rémunération mensuelle brute de ${fmtNum(d.salary)} FCFA.`, style: "body" },
    { text: "Article 4 — Obligations : Le Salarié s'engage à exécuter ses fonctions avec loyauté et diligence.", style: "body" },
    { text: "Article 5 — Confidentialité : Le Salarié s'engage au strict respect de la confidentialité.", style: "body" },
    { text: `Fait à Cotonou, en deux exemplaires, le ${todayFr()}.`, style: "body" },
  ];
}

function tplAttestationMembre(d) {
  return [
    { text: `Je soussigné, KEKERE Pamphile Dotou, Propriétaire et Gérant du club AMAL VBC, atteste que ${d.member_name || "—"} est ${d.member_type || "Adhérent"} du club depuis le ${d.since || todayFr()}.`, style: "body" },
    { text: "Cette attestation est délivrée pour servir et valoir ce que de droit.", style: "body" },
  ];
}

function tplCarteMembre(d) {
  return [
    {
      table: {
        widths: ["*"],
        body: [
          [{ text: "CARTE DE MEMBRE", style: "tableHeader", alignment: "center" }],
          [
            {
              stack: [
                { text: d.member_name || "—", fontSize: 14, bold: true, margin: [0, 5, 0, 3] },
                { text: `Catégorie : ${d.member_type || "—"}`, fontSize: 10 },
                { text: `N° Licence : ${d.license_number || "—"}`, fontSize: 10 },
                { text: `Valide jusqu'au : ${d.valid_until || "—"}`, fontSize: 10 },
              ],
              margin: [10, 10, 10, 10],
            },
          ],
        ],
      },
      layout: { hLineColor: () => GREEN, vLineColor: () => GREEN },
    },
  ];
}

function tplOrdreMission(d) {
  return [
    { text: `Le club AMAL VBC ordonne à ${d.employee_name || "—"} (${d.function || "—"}) de se rendre à ${d.destination || "—"}.`, style: "body" },
    { text: `Objet de la mission : ${d.purpose || "—"}`, style: "body" },
    { text: `Période : du ${d.start_date || "—"} au ${d.end_date || "—"}`, style: "body" },
    { text: "Tous les frais inhérents à cette mission sont à la charge du club.", style: "body" },
  ];
}

function tplAttestationPresence(d) {
  return [
    { text: `Je soussigné, atteste que ${d.person_name || "—"} a effectivement été présent(e) à l'événement « ${d.event || "—"} » organisé le ${d.event_date || todayFr()}.`, style: "body" },
    { text: "Cette attestation est délivrée pour servir et valoir ce que de droit.", style: "body" },
  ];
}

function tplAttestationSportive(d) {
  return [
    { text: `Je soussigné, KEKERE Pamphile Dotou, Propriétaire et Gérant du club AMAL VBC, atteste que ${d.player_name || "—"} est licencié(e) au club dans la catégorie ${d.category || "—"} pour la saison ${d.season || "2025-2026"}.`, style: "body" },
    { text: "Cette attestation est délivrée pour servir et valoir ce que de droit.", style: "body" },
  ];
}

function tplConventionPartenariat(d) {
  return [
    { text: "Entre les soussignés :", style: "body", bold: true },
    { text: "Le club AMAL VBC, représenté par son Propriétaire KEKERE Pamphile Dotou, ci-après dénommé « le Club »,", style: "body" },
    { text: `Et ${d.partner_name || "—"}, ci-après dénommé « le Partenaire »,`, style: "body" },
    { text: "Il a été convenu ce qui suit :", style: "body", bold: true },
    { text: `Article 1 : Le Partenaire s'engage à soutenir le Club à hauteur de ${fmtNum(d.amount)} FCFA pendant une durée de ${d.duration || "12 mois"}.`, style: "body" },
    { text: "Article 2 : En contrepartie, le Club valorisera le Partenaire sur ses supports de communication.", style: "body" },
    { text: "Article 3 : Tout différend sera réglé à l'amiable ou devant les tribunaux compétents de Cotonou.", style: "body" },
    { text: `Fait à Cotonou, en deux exemplaires, le ${todayFr()}.`, style: "body" },
  ];
}

function infoTable(rows) {
  return {
    table: {
      widths: [160, "*"],
      body: rows.map(([k, v]) => [{ text: k, style: "tableLabel" }, { text: String(v ?? "—"), fontSize: 10 }]),
    },
    layout: {
      hLineColor: () => "#E2E8F0",
      vLineColor: () => "#E2E8F0",
      fillColor: (i) => (i % 2 === 0 ? "white" : "#F1F5F9"),
    },
  };
}

function tplFicheJoueur(d) {
  const nom = `${d.first_name || ""} ${d.last_name || ""}`.trim() || "—";
  return [
    infoTable([
      ["Nom complet", nom],
      ["Date de naissance", d.date_of_birth || "—"],
      ["Catégorie", d.category || "—"],
      ["Poste", d.position || "—"],
      ["Taille / Poids", `${d.height_cm || "—"} cm / ${d.weight_kg || "—"} kg`],
      ["N° de licence", d.license_number || "—"],
      ["N° de maillot", d.jersey_number || "—"],
      ["Club précédent", d.previous_club || "—"],
      ["Statut", d.status || "—"],
    ]),
  ];
}

function tplFicheSalarie(d) {
  const nom = `${d.first_name || ""} ${d.last_name || ""}`.trim() || "—";
  return [
    infoTable([
      ["Nom complet", nom],
      ["Fonction", d.function || "—"],
      ["Catégorie", d.staff_type || "—"],
      ["Date d'embauche", d.hire_date || "—"],
      ["Type de contrat", d.contract_type || "—"],
      ["Salaire mensuel", `${fmtNum(d.salary)} FCFA`],
      ["Téléphone", d.phone || "—"],
      ["Email", d.email || "—"],
    ]),
  ];
}

function tplFichePaie(d) {
  const base = parseFloat(d.base_salary) || 0;
  const primes = parseFloat(d.bonus) || 0;
  const ded = parseFloat(d.deductions) || 0;
  const net = base + primes - ded;
  return [
    infoTable([
      ["Salarié", d.employee_name || "—"],
      ["Fonction", d.function || "—"],
      ["Mois", d.month || todayFr()],
    ]),
    { text: " ", margin: [0, 5, 0, 0] },
    {
      table: {
        widths: ["*", 100],
        body: [
          [{ text: "Désignation", style: "tableHeader" }, { text: "Montant (FCFA)", style: "tableHeader", alignment: "right" }],
          ["Salaire de base", { text: fmtNum(base), alignment: "right" }],
          ["Primes / Indemnités", { text: fmtNum(primes), alignment: "right" }],
          ["Déductions", { text: `-${fmtNum(ded)}`, alignment: "right" }],
          [
            { text: "NET À PAYER", bold: true, fillColor: GREEN, color: "white" },
            { text: `${fmtNum(net)} FCFA`, bold: true, fillColor: GREEN, color: "white", alignment: "right" },
          ],
        ],
      },
      layout: { hLineColor: () => GREEN, vLineColor: () => GREEN },
    },
  ];
}

const TITLES = {
  attestation_don: "Attestation de don",
  recu_don: "Reçu de don",
  contrat_travail: "Contrat de travail",
  attestation_membre: "Attestation de membre",
  carte_membre: "Carte de membre",
  ordre_mission: "Ordre de mission",
  attestation_presence: "Attestation de présence",
  attestation_sportive: "Attestation sportive",
  convention_partenariat: "Convention de partenariat",
  fiche_joueur: "Fiche joueur",
  fiche_salarie: "Fiche salarié",
  fiche_paie: "Fiche de paie",
};

const BUILDERS = {
  attestation_don: tplAttestationDon,
  recu_don: tplRecuDon,
  contrat_travail: tplContratTravail,
  attestation_membre: tplAttestationMembre,
  carte_membre: tplCarteMembre,
  ordre_mission: tplOrdreMission,
  attestation_presence: tplAttestationPresence,
  attestation_sportive: tplAttestationSportive,
  convention_partenariat: tplConventionPartenariat,
  fiche_joueur: tplFicheJoueur,
  fiche_salarie: tplFicheSalarie,
  fiche_paie: tplFichePaie,
};

function genDocNumber(type) {
  const d = new Date();
  const ts = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}-${String(d.getHours()).padStart(2, "0")}${String(d.getMinutes()).padStart(2, "0")}${String(d.getSeconds()).padStart(2, "0")}`;
  const prefix = type.split("_")[0].slice(0, 3).toUpperCase();
  return `AMAL-${prefix}-${ts}`;
}

export async function generatePdfClient(type, data) {
  const builder = BUILDERS[type];
  if (!builder) throw new Error(`Type de document inconnu : ${type}`);
  const docNumber = genDocNumber(type);
  const logoData = await getLogoDataUrl();
  const content = builder(data || {}, docNumber);
  const docDef = buildDocDef(TITLES[type], docNumber, content, logoData, {
    noSign: ["carte_membre", "ordre_mission"].includes(type),
  });
  return new Promise((resolve, reject) => {
    try {
      pdfMake.createPdf(docDef).download(`${type}.pdf`, () => resolve(true));
    } catch (e) {
      reject(e);
    }
  });
}
